"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EntitlementWorkspaceBehaviorForKey = exports.EntitlementTypeForKey = exports.EntitlementKey = void 0;
const zod_1 = require("zod");
/**
 * The main list of entitlements
 *
 * To add a new entitlement, add it to this list first, then assign it a type in EntitlementTypeForKey below
 */
exports.EntitlementKey = zod_1.z.enum([
    /**
     * An informational entitlement that provides a PaymentPlanCategory for the account's configured plans, which can
     * be used when interfacing with legacy systems that still expect to find a account's plan category from the
     * activeProductUid field
     *
     * This is more accurate than activeProductUid for the following reasons:
     *  - It excludes canceled plans; activeProductUid does not get cleared when a plan is no longer contributing to
     *    entitlements
     *  - It uses matchers that are aware of the plan epoch, so it will correctly return the "noPlan" category for a new
     *    user, and the userLegacyFree category for a legacy user
     */
    "category",
    /**
     * Whether the account is allowed to export projects to Gerber format
     */
    "export_basic_formats",
    /**
     * The number of free copilot credits a user is given each calendar month
     */
    "copilot_free_credits",
    /**
     * The default spending limit a user has before they configure their own custom spending limit
     */
    "copilot_default_spending_limit",
    /**
     * Whether the user is allowed to use paid copilot credits
     */
    "copilot_can_use_paid_credits",
    /**
     * The type of price used for paid Copilot Credits
     */
    "copilot_paid_credit_price_type",
    /**
     * Whether the account is allowed to import projects in Altium format
     */
    "import_altium",
    /**
     * Whether the account is allowed to import projects in EDIF format
     */
    "import_edif",
    /**
     * The maximum number of private projects the account is allowed to have
     */
    "max_private_projects",
    /**
     * Access to the PCB AutoLayout feature
     *
     * Access to this feature is also granted by a parallel feature flag: you have access if you have _either_ this
     * entitlement, or the feature flag.
     */
    "pcb_auto_layout",
    /**
     * Whether the account is allowed to edit private projects. This is distinct
     * from max_private_projects to solve the issue of "can no longer edit
     * projects that are not public" applying to projects where the user isn't
     * the creator for new-epoch users, while leaving existing behavior the same
     * for legacy users.
     */
    "edit_private_projects",
    /**
     * The maximum number of invited editors the account is allowed to have
     * across all projects
     */
    "max_editors",
]);
/**
 * A total mapping of each entitlement key to an EntitlementType
 */
exports.EntitlementTypeForKey = {
    category: "planCategory",
    export_basic_formats: "boolean",
    copilot_free_credits: "creditAmount",
    copilot_default_spending_limit: "spendingLimit",
    copilot_can_use_paid_credits: "boolean",
    copilot_paid_credit_price_type: "copilotPaidCreditPriceType",
    import_altium: "boolean",
    import_edif: "boolean",
    max_private_projects: "largestNumber",
    pcb_auto_layout: "boolean",
    edit_private_projects: "boolean",
    max_editors: "largestNumber",
};
/**
 * Configures the behavior of entitlements when both user and workspace (organization/enterprise) plans contribute to
 * an entitlement
 *
 * A total mapping of each entitlement key to an EntitlementWorkspaceBehavior
 *
 * See EntitlementWorkspaceBehavior for a detailed description of each behavior
 */
exports.EntitlementWorkspaceBehaviorForKey = {
    category: "override",
    copilot_free_credits: "override",
    copilot_default_spending_limit: "override",
    copilot_can_use_paid_credits: "override",
    copilot_paid_credit_price_type: "override",
    max_private_projects: "override",
    max_editors: "override",
    export_basic_formats: "merge",
    import_altium: "merge",
    import_edif: "merge",
    pcb_auto_layout: "merge",
    edit_private_projects: "merge",
};
//# sourceMappingURL=entitlements.js.map